import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, Stack, Avatar, Chip, Divider, CircularProgress, Alert
} from '@mui/material';
import apiService from '../../services/apiService';
import {ROLES} from '../../config/roles';

const StatValue = ({label, value}) => (
    <Box sx={{textAlign: 'center'}}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{value}</Typography>
    </Box>
);

const MinigameStatCard = ({stat, onClick}) => (
    <Paper
        elevation={2}
        sx={{p: 2, cursor: 'pointer', transition: 'box-shadow 0.2s', '&:hover': {boxShadow: 6}}}
        onClick={onClick}
    >
        <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 1.5}}>
            <Avatar src={stat.iconUrl} alt={stat.name} sx={{width: 40, height: 40}}/>
            <Box sx={{flex: 1}}>
                <Typography variant="h6">{stat.name}</Typography>
                <Typography variant="caption" color="text.secondary">{stat.identifier}</Typography>
            </Box>
            <Chip
                label={stat.enabled ? 'Enabled' : 'Disabled'}
                color={stat.enabled ? 'success' : 'default'}
                size="small"
            />
        </Stack>
        <Divider sx={{mb: 1.5}}/>
        <Grid container spacing={2}>
            <Grid size={{xs: 6, sm: 3}}>
                <StatValue label="Total Plays" value={stat.totalPlays.toLocaleString()}/>
            </Grid>
            <Grid size={{xs: 6, sm: 3}}>
                <StatValue label="Players" value={stat.uniquePlayers.toLocaleString()}/>
            </Grid>
            <Grid size={{xs: 6, sm: 3}}>
                <StatValue label="Win Rate" value={`${stat.winRate.toFixed(1)}%`}/>
            </Grid>
            <Grid size={{xs: 6, sm: 3}}>
                <StatValue label="Jackpots" value={stat.totalJackpots.toLocaleString()}/>
            </Grid>
        </Grid>
    </Paper>
);

const MinigameStatsPage = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        apiService.get('/api/minigames/stats')
            .then(setStats)
            .catch(err => setError(err.message || 'Failed to load statistics'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;
    if (error) return <Alert severity="error" sx={{m: 2}}>{error}</Alert>;

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Minigame Statistics</Typography>

            {stats.length === 0 ? (
                <Alert severity="info">No minigames found.</Alert>
            ) : (
                <Grid container spacing={3}>
                    {stats.map(stat => (
                        <Grid key={stat.minigameId} size={{xs: 12, md: 6}}>
                            <MinigameStatCard
                                stat={stat}
                                onClick={() => navigate(`/minigames/stats/${stat.minigameId}`)}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

MinigameStatsPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
};

export default MinigameStatsPage;
